import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "@/components/Reveal";
import EventsPanel from "@/components/admin/EventsPanel";
import DjsPanel from "@/components/admin/DjsPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogOut, RefreshCw, Inbox, Trash2, Star, Copy, CalendarDays, MessageSquareText } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";

const CHART_COLORS = ["#D4AF37", "#FF007F", "#8B7FD4", "#4FB3A9", "#D98E4A", "#7E7E8A"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "de_admin_token";

const inputCls =
  "bg-white/5 border-white/10 h-12 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  const fetchBookings = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load enquiries.");
      }
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch (e) {
      toast.error("Could not load reviews.");
    }
  }, []);

  const fetchStats = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStats(data);
    } catch (e) {}
  }, []);

  const remove = async (kind, id) => {
    try {
      await axios.delete(`${API}/${kind}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (kind === "bookings") setBookings(bookings.filter((b) => b.id !== id));
      else setReviews(reviews.filter((r) => r.id !== id));
      fetchStats(token);
      toast.success("Deleted.");
    } catch (e) {
      toast.error("Could not delete. Please try again.");
    }
  };

  useEffect(() => {
    if (token) {
      fetchBookings(token);
      fetchReviews();
      fetchStats(token);
    }
  }, [token, fetchBookings, fetchReviews, fetchStats]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
  };

  if (!token) {
    return (
      <main data-testid="admin-page" className="min-h-screen flex items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="glass rounded-3xl p-10 sm:p-12 w-full max-w-md space-y-8"
        >
          <div>
            <Overline>Owner Access</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4">
              Booking <em className="text-primary">enquiries.</em>
            </h1>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              data-testid="admin-email-input"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@dreamentertainment.org"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              data-testid="admin-password-input"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full rounded-full bg-primary text-primary-foreground py-4 text-sm font-medium tracking-widest uppercase transition-[transform,opacity] duration-300 hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen pt-32 pb-24 px-6 sm:px-12 lg:px-20 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <Overline>Owner Dashboard</Overline>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4">
            Booking <em className="text-primary">enquiries.</em>
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-btn"
            onClick={() => {
              fetchBookings(token);
              fetchReviews();
              fetchStats(token);
            }}
            className="rounded-full border border-white/20 px-5 py-2.5 text-sm flex items-center gap-2 hover:border-primary hover:text-primary transition-colors duration-300"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="rounded-full border border-white/20 px-5 py-2.5 text-sm flex items-center gap-2 hover:border-accent hover:text-accent transition-colors duration-300"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
        {[
          ["overview", "Overview"],
          ["enquiries", "Enquiries"],
          ["reviews", "Reviews"],
          ["events", "Events"],
          ["djs", "Staff"],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`rounded-full px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
              tab === id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <section data-testid="admin-stats" className="mb-16 space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Enquiries", value: stats.total_enquiries, icon: Inbox, tid: "stat-total-enquiries" },
              { label: "Enquiries This Month", value: stats.enquiries_this_month, icon: CalendarDays, tid: "stat-month-enquiries" },
              { label: "Total Reviews", value: stats.total_reviews, icon: MessageSquareText, tid: "stat-total-reviews" },
              { label: "Average Rating", value: stats.avg_rating || "N/A", icon: Star, tid: "stat-avg-rating" },
            ].map((s) => (
              <div key={s.label} data-testid={s.tid} className="glass rounded-2xl p-6">
                <s.icon size={20} className="text-primary" />
                <p className="font-serif text-4xl mt-3">{s.value}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <Overline>Enquiries Over Time</Overline>
              <div className="mt-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.by_month}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: "#8a8a92", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: "#8a8a92", fontSize: 11 }} axisLine={false} tickLine={false} width={24} />
                    <Tooltip contentStyle={{ background: "#14141c", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, fontSize: 12 }} cursor={{ fill: "rgba(212,175,55,0.06)" }} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <Overline>Event Types</Overline>
              {stats.event_types.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-16">
                  No enquiries yet. This fills in as bookings arrive.
                </p>
              ) : (
                <div className="flex items-center mt-4 gap-4">
                  <ResponsiveContainer width="55%" height={220}>
                    <PieChart>
                      <Pie data={stats.event_types} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} strokeWidth={0}>
                        {stats.event_types.map((e, i) => (
                          <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#14141c", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <ul className="flex-1 space-y-2">
                    {stats.event_types.map((e, i) => (
                      <li key={e.name} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {e.name}
                        <span className="text-foreground ml-auto">{e.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <Overline>Packages Chosen</Overline>
              {stats.packages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-16">
                  No enquiries yet. This fills in as bookings arrive.
                </p>
              ) : (
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stats.packages} layout="vertical" margin={{ left: 10 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" width={170} tick={{ fill: "#c9c9cf", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: "#14141c", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, fontSize: 12 }} cursor={{ fill: "rgba(255,0,127,0.06)" }} />
                      <Bar dataKey="value" fill="#FF007F" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="glass rounded-2xl p-6">
              <Overline>Review Ratings</Overline>
              <div className="mt-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.ratings}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="stars" tick={{ fill: "#8a8a92", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: "#8a8a92", fontSize: 11 }} axisLine={false} tickLine={false} width={24} />
                    <Tooltip contentStyle={{ background: "#14141c", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, fontSize: 12 }} cursor={{ fill: "rgba(212,175,55,0.06)" }} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === "enquiries" && (bookings === null ? (
        <div className="flex justify-center py-24">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      ) : bookings.length === 0 ? (
        <div data-testid="admin-empty-state" className="text-center py-24 text-muted-foreground">
          <Inbox size={40} className="mx-auto mb-4 text-primary/50" />
          <p className="font-serif text-2xl">No enquiries yet.</p>
          <p className="text-sm mt-2">New booking form submissions will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-bookings-list">
          {bookings.map((b) => (
            <article
              key={b.id}
              data-testid={`admin-booking-${b.id}`}
              className="glass rounded-2xl p-6 sm:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl">{b.name}</h2>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-primary mt-1">
                    {b.event_type}
                  </p>
                </div>
                <div className="text-right text-sm text-muted-foreground flex items-start gap-4">
                  <div>
                    <p>Event: {b.event_date}</p>
                    <p className="text-xs mt-1">
                      Enquired {new Date(b.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    data-testid={`admin-delete-booking-${b.id}`}
                    onClick={() => remove("bookings", b.id)}
                    aria-label="Delete enquiry"
                    className="text-muted-foreground/60 hover:text-accent transition-colors duration-300 p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 text-sm text-foreground/80">
                <p>
                  <span className="text-muted-foreground">Email:</span>{" "}
                  <a href={`mailto:${b.email}`} className="text-primary hover:text-accent transition-colors duration-300">
                    {b.email}
                  </a>
                </p>
                <p>
                  <span className="text-muted-foreground">Phone:</span>{" "}
                  <a href={`tel:${b.phone}`} className="text-primary hover:text-accent transition-colors duration-300">
                    {b.phone}
                  </a>
                </p>
                <p>
                  <span className="text-muted-foreground">Venue:</span> {b.venue || "N/A"}
                  {b.guests ? ` · ${b.guests} guests` : ""}
                </p>
              </div>
              {(b.package || b.budget) && (
                <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-sm text-foreground/80">
                  {b.package && (
                    <p>
                      <span className="text-muted-foreground">Package:</span>{" "}
                      <span className="text-primary">{b.package}</span>
                    </p>
                  )}
                  {b.budget && (
                    <p>
                      <span className="text-muted-foreground">Budget:</span> {b.budget}
                    </p>
                  )}
                </div>
              )}
              {b.addons && b.addons.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.addons.map((a) => (
                    <span
                      key={a}
                      className="text-[10px] tracking-widest uppercase border border-primary/30 text-primary rounded-full px-3 py-1"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}
              {b.message && (
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-4">
                  {b.message}
                </p>
              )}
            </article>
          ))}
        </div>
      ))}
      {tab === "reviews" && (<>
      {/* REVIEW INVITE */}
      <div className="glass rounded-2xl p-6 mt-16 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Overline>Grow Your Reviews</Overline>
          <p className="text-sm text-muted-foreground mt-2 max-w-md leading-relaxed">
            Text this link to past clients after their event. Their review
            goes live on the Testimonials page instantly.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <code
            data-testid="admin-review-invite-link"
            className="text-xs text-primary bg-white/5 border border-white/10 rounded-full px-4 py-2.5"
          >
            {window.location.origin}/review
          </code>
          <button
            data-testid="admin-copy-invite-btn"
            onClick={async () => {
              const link = `${window.location.origin}/review`;
              try {
                await navigator.clipboard.writeText(link);
              } catch {
                const ta = document.createElement("textarea");
                ta.value = link;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
              }
              toast.success("Review link copied. Text it to your clients!");
            }}
            className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm flex items-center gap-2 transition-transform duration-300 hover:scale-105"
          >
            <Copy size={14} /> Copy Link
          </button>
        </div>
      </div>

      {/* REVIEWS */}
      <div className="mt-20">
        <Overline>Live Reviews</Overline>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 mb-8">
          Guest <em className="text-primary">reviews.</em>
        </h2>
        {reviews === null ? (
          <div className="flex justify-center py-12">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        ) : reviews.length === 0 ? (
          <p data-testid="admin-reviews-empty" className="text-muted-foreground text-sm py-8 text-center">
            No reviews yet. New ones appear here and on the Testimonials page instantly.
          </p>
        ) : (
          <div className="space-y-4" data-testid="admin-reviews-list">
            {reviews.map((r) => (
              <article
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="glass rounded-2xl p-6 flex items-start justify-between gap-6"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-sm">{r.name}</p>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, s) => (
                        <Star
                          key={s}
                          size={11}
                          className={
                            s < r.rating
                              ? "fill-primary text-primary"
                              : "text-muted-foreground/30"
                          }
                        />
                      ))}
                    </div>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                      {r.event_type || "Dream Event"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-3xl">
                    {r.text}
                  </p>
                </div>
                <button
                  data-testid={`admin-delete-review-${r.id}`}
                  onClick={() => remove("reviews", r.id)}
                  aria-label="Delete review"
                  className="shrink-0 text-muted-foreground/60 hover:text-accent transition-colors duration-300 p-1"
                >
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
      </>)}

      {tab === "events" && <EventsPanel token={token} />}
      {tab === "djs" && <DjsPanel token={token} />}
    </main>
  );
}
